import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { omit } from 'lodash';
import { Subscription } from 'rxjs';
import {
  getShortcutName,
  toolNameForEventTracking,
} from '../../../../../cross-team-shared/annotation/shared/abstract-tool';
import { SvgIcon } from '../../../../../cross-team-shared/icons/create-icon-definition';
import { getOrThrow } from '../../../../../cross-team-shared/misc-utils/strict-record';
import { ToolName } from '../../../../../cross-team-shared/tools-names';
import { EventTrackingService } from '../../../../../shared/event-tracking/event-tracking.service';
import { getOutputByName } from '../../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { getSelectedToolStore } from '../../../../h3ml/state/task-workspace-store-utils';
import { isKeyboardShortcutName } from '../../../../shared/keyboard-shortcuts/keyboard-shortcuts';
import { RootKeyboardShortcutService } from '../../../../shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { FusionManagerFactory } from '../fusion/fusion-manager-factory.service';

interface ToolButtonBaseConfig extends Omit<SvgIcon, 'svg'> {
  tooltip: string;
}

export interface ToolButtonConfig<T extends ToolName>
  extends ToolButtonBaseConfig {
  toolName: T;
}

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export abstract class AbstractMainToolbarComponent<
    ToolNameType extends ToolName,
  >
  extends DirectiveSuperclass
  implements OnDestroy
{
  protected readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  private readonly fusionManagerFactory = inject(FusionManagerFactory);
  private readonly keyboardShortcutService = inject(
    RootKeyboardShortcutService,
  );
  private eventTrackingService = inject(EventTrackingService);
  private registeredShortcuts: Subscription[] = [];
  protected abstract readonly buttonsBaseConfig: StrictRecord<
    ToolNameType,
    ToolButtonBaseConfig
  >;

  setTool(name: ToolName) {
    this.getContentConnector().setTool(name);
  }

  ngOnDestroy() {
    this.unsubscribeShortcuts();
  }

  protected buildButtonsConfig(
    outputName: string,
  ): Array<ToolButtonConfig<ToolNameType>> {
    const toolNames = this.getOrderedToolNames();
    this.setSelectedTool(toolNames, outputName);
    this.unsubscribeShortcuts();

    return toolNames.map((toolName) => {
      const config = getOrThrow(this.buttonsBaseConfig, toolName);

      const shortcutName = getShortcutName(toolName);
      this.setupKeyboardShortcut(toolName);
      assert(isKeyboardShortcutName(shortcutName), "shortcut doesn't exist");
      return {
        toolName,
        ...config,
        tooltip: `${config.tooltip} ${this.keyboardShortcutService.tooltipSuffix(shortcutName)}`,
      };
    });
  }

  protected setSelectedTool(toolNames: ToolNameType[], outputName: string) {
    const selectedToolStore = getSelectedToolStore(
      this.taskWorkspaceStore,
      outputName,
    );
    this.setTool(selectedToolStore.state()?.name ?? toolNames[0]);
  }

  private getContentConnector() {
    const { selectedOutputName, project } = this.taskWorkspaceStore.state();
    const output = getOutputByName(project, selectedOutputName);
    const minion = this.fusionManagerFactory
      .getManager(output.id)
      .getMinion(output.id);
    assert(minion, 'minion should always be defined');
    return minion.contentConnector;
  }

  private setupKeyboardShortcut(toolName: ToolName) {
    const subscription$ = this.keyboardShortcutService
      .getEvent$(getShortcutName(toolName))
      .subscribe(({ eventTrackingValue }) => {
        this.setTool(toolName);
        this.eventTrackingService.track({
          id: 'select_main_tool',
          bucket: this.getTrackingEventBucket(toolName),
          ...(toolName === 'Measure' && { sub_bucket: 'frame' }),
          ...eventTrackingValue,
          tool: toolNameForEventTracking(toolName),
        });
      });
    this.registeredShortcuts.push(subscription$);
  }

  private getTrackingEventBucket(name: ToolName) {
    switch (name) {
      case 'Measure':
      case 'Zoom':
        return 'navigation';
      case 'Edit':
      case 'Select':
        return 'adjustment';
      default:
        return 'annotation';
    }
  }

  private unsubscribeShortcuts() {
    this.registeredShortcuts.forEach((shortcut$) => {
      shortcut$.unsubscribe();
    });
  }

  protected abstract getOrderedToolNames(): ToolNameType[];
}

export function fromSvgIcon(icon: SvgIcon) {
  return omit(icon, 'svg');
}
